import { useState } from "react";
import FuelLoading from "./FuelLoading";
import "./FuelPublicFeed.scss";
import { useGetPublicDonationsQuery } from "../features/api/donationApiSlice";

const OPTION_THEMES = {
  gallon: { icon: "⛽", label: "Gallon of Fuel" },
  half_tank: { icon: "🗺️", label: "Half Tank Detour" },
  full_tank: { icon: "🚀", label: "Full Day of Adventure" },
  custom: { icon: "🎨", label: "Custom Boost" },
};

export default function FuelPublicFeed() {
  const [page, setPage] = useState(1);
  const limit = 10; // Number of items loaded per batch

  // Fetching data cleanly using RTK Query
  const { data, isLoading, isFetching, isError } = useGetPublicDonationsQuery({
    page,
    limit,
  });

  const formatAmount = (cents) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Safe variables derived from RTK cache results
  const donations = data?.items || [];
  const totalPages = data?.totalPages || 1;
  const hasMore = page < totalPages;

  const handleLoadMore = () => {
    if (hasMore && !isFetching) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // First time layout container loading state
  if (isLoading && page === 1) {
    return (
      <div className="fuel-feed-loading">
        <FuelLoading text="Gathering notes from the road..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="fuel-feed-error">
        ⚠️ Could not load the road feed. Please try refreshing.
      </div>
    );
  }

  return (
    <section className="fuel-feed-section">
      <div className="fuel-feed-header">
        <h2>Community Road Notes</h2>
        <p>Recent fuel contributions keeping the wheels spinning.</p>
      </div>

      {donations.length === 0 ? (
        <div className="fuel-feed-empty">
          <p>
            The road feed is currently empty. Be the first to leave a note! 🗺️
          </p>
        </div>
      ) : (
        <div className="fuel-feed-grid">
          {donations.map((donation, index) => {
            const theme =
              OPTION_THEMES[donation.fuelOptionType] || OPTION_THEMES.custom;

            return (
              <div
                key={donation._id || index}
                className="fuel-feed-card fuel-fade-in"
              >
                <div className="feed-card-top">
                  <div className="donor-profile">
                    <span
                      className="donor-icon"
                      role="img"
                      aria-label={theme.label}
                    >
                      {theme.icon}
                    </span>
                    <div className="donor-details">
                      <h3>{donation.donorName}</h3>
                      <span className="donation-meta">
                        Fueled {theme.label} • {formatDate(donation.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="donation-tag">
                    {formatAmount(donation.amountInCents)}
                  </div>
                </div>

                {donation.message && (
                  <div className="feed-card-body">
                    <p>“{donation.message}”</p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Dynamic Pagination Loader Controls */}
          <div className="feed-pagination-box">
            {isFetching && page > 1 && (
              <div className="mini-feed-loader">
                <FuelLoading text="Chasing down older logs..." />
              </div>
            )}

            {hasMore && !isFetching && (
              <button
                type="button"
                className="fuel-secondary-btn load-more-btn"
                onClick={handleLoadMore}
              >
                🛣️ Load Older Road Notes
              </button>
            )}

            {!hasMore && donations.length > 0 && (
              <p className="end-of-feed-text">
                You have reached the beginning of the journey. 🏁
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
